import React from 'react';
import { toast } from './Toast';

// Cópia de uma coluna inteira para a área de transferência.
//
// Existe porque o navegador não permite selecionar uma coluna com o mouse numa
// tabela HTML: a seleção segue a ordem do texto na página, então arrastar para
// baixo sempre pega a linha inteira. Isso não é limitação do sistema, é como
// qualquer tabela HTML se comporta — por isso a saída é um botão, não um ajuste
// de CSS.
//
// Uso numa view:
//
//   this.copia = criarCopiaDeColuna();
//   ...
//   <DataTable getTableProps={this.copia.capturarLinhasVisiveis}
//              columns={[ this.copia.coluna('Nome', 'nome_funcionario') ]} />
//
// Para colunas com Cell própria, usar só o cabeçalho:
//
//   { Header: this.copia.cabecalho('Valor', 'valor', formatarMoeda),
//     accessor: 'valor', Cell: ... }
//
// O formatador é opcional e serve para o valor copiado sair igual ao que está
// na tela (data em dd/mm/aaaa, moeda com R$, etc.) em vez do valor cru do banco.

const copiarTexto = (texto) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(texto);
  }

  // Navegador antigo, ou página servida sem HTTPS (onde a Clipboard API não
  // existe): cai no textarea temporário, que funciona em qualquer contexto.
  return new Promise((resolve, reject) => {
    const campo = document.createElement('textarea');
    campo.value = texto;
    campo.style.position = 'fixed';
    campo.style.opacity = '0';
    document.body.appendChild(campo);
    campo.select();

    try {
      document.execCommand('copy');
      resolve();
    } catch (e) {
      reject(e);
    } finally {
      document.body.removeChild(campo);
    }
  });
};

export const criarCopiaDeColuna = () => {
  // Fica fora do state de propósito: quem preenche isso é o getTableProps, que
  // o react-table chama durante o render — um setState ali entraria em loop.
  let linhasVisiveis = [];

  const capturarLinhasVisiveis = (state) => {
    linhasVisiveis = (state && state.sortedData) || [];
    return {};
  };

  const copiarColuna = (titulo, accessor, formatar) => {
    if (linhasVisiveis.length === 0) {
      toast.error('Não há linhas para copiar.');
      return;
    }

    // sortedData é o conjunto já filtrado e ordenado, e não só a página aberta:
    // copiar 20 de 400 linhas seria pior do que não ter o botão.
    const valores = linhasVisiveis.map((linha) => {
      const original = linha._original || linha;
      const valor = original[accessor];
      const texto = formatar ? formatar(valor) : valor;
      return texto === null || texto === undefined ? '' : String(texto);
    });

    copiarTexto(valores.join('\n'))
      .then(() =>
        toast.success(
          `${valores.length} valor(es) da coluna ${titulo} copiado(s).`
        )
      )
      .catch(() => toast.error('Não foi possível copiar a coluna.'));
  };

  // O stopPropagation evita que clicar no ícone também ordene a coluna.
  const cabecalho = (titulo, accessor, formatar) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {titulo}
      <span
        title={`Copiar a coluna ${titulo}`}
        style={{ cursor: 'pointer', opacity: 0.55, fontSize: 14 }}
        onClick={(evento) => {
          evento.stopPropagation();
          copiarColuna(titulo, accessor, formatar);
        }}
      >
        ⧉
      </span>
    </span>
  );

  // Atalho para coluna simples de texto: cabeçalho com botão de copiar, largura
  // fixa (a rolagem horizontal cuida do resto) e valor em uma linha só, com
  // reticências e o texto completo no title.
  const coluna = (titulo, accessor, opcoes) => {
    const { width, formatar, ...resto } = opcoes || {};

    return {
      Header: cabecalho(titulo, accessor, formatar),
      accessor: accessor,
      width: width || 150,
      Cell: (row) => (
        <div
          className="action-cell"
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={
            row.value === null || row.value === undefined
              ? ''
              : String(row.value)
          }
        >
          {formatar ? formatar(row.value) : row.value}
        </div>
      ),
      ...resto,
    };
  };

  // As linhas como a tabela está mostrando agora (filtro, busca e ordem
  // aplicados, todas as páginas), no formato original dos dados. Serve para
  // exportar exatamente o que a pessoa está vendo.
  const linhas = () => linhasVisiveis.map((linha) => linha._original || linha);

  return { capturarLinhasVisiveis, copiarColuna, cabecalho, coluna, linhas };
};

export default criarCopiaDeColuna;
